import { BracketSlot as ApiBracketSlot } from 'services/brackets'
import { Game } from 'types/games'
import { Team } from 'types/teams'

const SIDE_STAGE_NAMES = ['1/32', '1/16', '1/8', '1/4', '1/2'] as const
const CENTER_STAGE_NAMES = ['3d place', 'Final'] as const

export type BracketCell = {
    id: number
    slotIndex: number
    game: Game | null
    team: Team | null
}

export type BracketSideRound = {
    stageName: string
    depth: number
    start: BracketCell[]
    end: BracketCell[]
}

export type BracketModel = {
    sideRounds: BracketSideRound[]
    centerMatches: BracketCell[]
    rows: number
    columns: number
    isEmpty: boolean
}

const STAGE_MATCH_COUNTS: Record<string, number> = {
    '1/32': 32,
    '1/16': 16,
    '1/8': 8,
    '1/4': 4,
    '1/2': 2,
    Final: 1,
    '3d place': 1,
}

function toCell(slot: ApiBracketSlot): BracketCell {
    return {
        id: slot.id,
        slotIndex: slot.slotIndex,
        game: slot.game ?? null,
        team: slot.team ?? null,
    }
}

function emptyCell(slotIndex: number): BracketCell {
    return { id: -slotIndex - 1, slotIndex, game: null, team: null }
}

function groupSlotsByStage(slots: ApiBracketSlot[]): Map<string, ApiBracketSlot[]> {
    const grouped = new Map<string, ApiBracketSlot[]>()

    for (const slot of slots) {
        const stageName = slot.stage?.name
        if (!stageName) continue
        const bucket = grouped.get(stageName) ?? []
        bucket.push(slot)
        grouped.set(stageName, bucket)
    }

    for (const [stageName, stageSlots] of Array.from(grouped.entries())) {
        grouped.set(
            stageName,
            [...stageSlots].sort((a, b) => a.slotIndex - b.slotIndex)
        )
    }

    return grouped
}

function resolveExpectedFirstRoundMatches(grouped: Map<string, ApiBracketSlot[]>, bracketSize?: number | null): number {
    if (bracketSize && bracketSize >= 2) {
        return bracketSize / 2
    }

    const firstSideStage = SIDE_STAGE_NAMES.find(name => grouped.has(name))
    if (firstSideStage) {
        return STAGE_MATCH_COUNTS[firstSideStage] ?? grouped.get(firstSideStage)!.length
    }

    return 1
}

function slotsForStage(stageSlots: ApiBracketSlot[], expectedCount: number): BracketCell[] {
    const cells: BracketCell[] = Array.from({ length: expectedCount }, (_, index) => emptyCell(index))

    for (const slot of stageSlots) {
        if (slot.slotIndex >= 0 && slot.slotIndex < expectedCount) {
            cells[slot.slotIndex] = toCell(slot)
        }
    }

    return cells
}

function splitStartEnd(slots: BracketCell[]): { start: BracketCell[]; end: BracketCell[] } {
    const half = slots.length / 2
    return {
        start: slots.slice(0, half),
        end: slots.slice(half),
    }
}

function resolveSideStages(expectedFirstRoundMatches: number): string[] {
    const firstStageIndex = SIDE_STAGE_NAMES.findIndex(name => STAGE_MATCH_COUNTS[name] === expectedFirstRoundMatches)
    if (firstStageIndex === -1) {
        return [...SIDE_STAGE_NAMES.slice(-1)]
    }
    return [...SIDE_STAGE_NAMES.slice(firstStageIndex)]
}

export function buildBracketModel(slots: ApiBracketSlot[], bracketSize?: number | null): BracketModel {
    const grouped = groupSlotsByStage(slots)
    const expectedFirstRoundMatches = resolveExpectedFirstRoundMatches(grouped, bracketSize)
    const sideStageNames = resolveSideStages(expectedFirstRoundMatches)

    const sideRounds: BracketSideRound[] = sideStageNames.map((stageName, depth) => {
        const expectedTotal = Math.max(1, expectedFirstRoundMatches / 2 ** depth)
        const cells = slotsForStage(grouped.get(stageName) ?? [], expectedTotal)
        const { start, end } = splitStartEnd(cells)

        return {
            stageName,
            depth,
            start,
            end,
        }
    })

    const centerMatches: BracketCell[] = CENTER_STAGE_NAMES.map(stageName => {
        const stageSlots = grouped.get(stageName) ?? []
        return slotsForStage(stageSlots, 1)[0]
    })

    return {
        sideRounds,
        centerMatches,
        rows: Math.max(1, expectedFirstRoundMatches / 2),
        columns: Math.max(3, sideRounds.length * 2 + 1),
        isEmpty: slots.length === 0,
    }
}

export function getStageLabel(stageName: string): string {
    switch (stageName) {
        case '1/32':
            return '1/32'
        case '1/16':
            return '1/16'
        case '1/8':
            return '1/8'
        case '1/4':
            return '1/4'
        case '1/2':
            return '1/2'
        case 'Final':
            return 'Final'
        case '3d place':
            return '3rd place'
        default:
            return stageName
    }
}
